import { Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Tabs } from "@narmi/design_system";
import PageLayout from "../../src/components/PageLayout";
import BackButton from "../../src/modules/transfers/wire/templates/TemplatesList/BackButton/BackButton";
import styles from "./Templates.module.scss";
import {
  ACH_TEMPLATES_PATH,
  areAchTemplatesAvailable,
  areDomesticWireTemplatesAvailable,
  areInternationalWireTemplatesAvailable,
  DOMESTIC_WIRE_TEMPLATES_PATH,
  INTERNATIONAL_WIRE_TEMPLATES_PATH,
  TEMPLATES_ROUTE,
} from "./constants";

type TemplatesContainerProps = {
  features: API.Features;
};

export type TabConfigProps = {
  id: string;
  path: string;
  labelKey: string;
  fallbackCopy: string;
  enabled?: (features: API.Features) => boolean;
};

export const TAB_CONFIG: TabConfigProps[] = [
  {
    id: "bulk-templates",
    path: ACH_TEMPLATES_PATH,
    labelKey: "ach-templates-tab",
    fallbackCopy: "ACH payment",
    enabled: (features: API.Features) =>
      Boolean(
        areAchTemplatesAvailable(features) &&
          features["indigo.BulkPaymentTemplateGroup_permission"]
      ),
  },
  {
    id: "domestic-wire",
    path: DOMESTIC_WIRE_TEMPLATES_PATH,
    labelKey: "domestic-wire-templates-tab",
    fallbackCopy: "Domestic wire",
    enabled: (features: API.Features) =>
      Boolean(
        areDomesticWireTemplatesAvailable(features) &&
          features["indigo.FedwireTemplate_permission"]
      ),
  },
  {
    id: "international-wire",
    path: INTERNATIONAL_WIRE_TEMPLATES_PATH,
    labelKey: "international-wire-templates-tab",
    fallbackCopy: "International wire",
    enabled: (features: API.Features) =>
      Boolean(
        areInternationalWireTemplatesAvailable(features) &&
          features["indigo.SwiftWireTemplate_permission"]
      ),
  },
];

const TemplatesContainer = ({ features }: TemplatesContainerProps) => {
  const { l10n } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();

  const visibleTabs = TAB_CONFIG.filter(
    (tab) => tab.enabled && tab.enabled(features)
  );

  const selectedIndex = visibleTabs.findIndex((tab) =>
    Boolean(matchPath(`/${TEMPLATES_ROUTE}/${tab.path}/*`, location.pathname))
  );

  const handleTabChange = (index: number) => {
    const tab = visibleTabs[index];
    if (tab) {
      navigate(`/${TEMPLATES_ROUTE}/${tab.path}`);
    }
  };

  return (
    <PageLayout>
      <div
        className="margin--top--xxl margin--bottom--xl"
        style={{ position: "relative" }}
      >
        {(visibleTabs[selectedIndex]?.id === "international-wire" ||
          visibleTabs[selectedIndex]?.id === "domestic-wire") &&
          new URLSearchParams(location.search).get("from") ===
            "template-selector" && (
            <BackButton
              onClick={() =>
                navigate(
                  visibleTabs[selectedIndex]?.id === "international-wire"
                    ? "/transfer/wires/international"
                    : "/transfer/wires"
                )
              }
              text={l10n.getString("templates-back-to-wires")}
              className={styles.backButton}
            />
          )}
        <h1>{l10n.getString("manage-templates-heading")}</h1>
      </div>
      <Tabs
        selectedIndex={selectedIndex === -1 ? 0 : selectedIndex}
        onTabChange={handleTabChange}
      >
        <Tabs.List>
          {visibleTabs.map((tab) => (
            <Tabs.Tab
              key={tab.id}
              label={l10n.getString(tab.labelKey, null, tab.fallbackCopy)}
              tabId={tab.id}
            />
          ))}
        </Tabs.List>
      </Tabs>
      <Outlet />
    </PageLayout>
  );
};

export default TemplatesContainer;
